import {
  BillingState,
  InvoiceLineItem_Read,
  InvoiceLineItem_Update,
  InvoiceLineItemNet_Read,
  InvoiceLineItemType,
  OrderUnitOfMeasure,
  PayCommissionPresentation_Read,
  RejectedReason,
  SettlementCommissionPresentation,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { LoadBasic } from '~hooks/useLoad/models';
import { BasicMaterial } from '~hooks/useMaterials/models';
import { CommissionSummary } from '~hooks/useSettlementLineItems/models';
import { SettlementBasic } from '~hooks/useSettlements/models';
import { Nullable } from '~types/Nullable';
import { toDecimal } from '~utils/decimal';

export class InvoiceLineItem {
  constructor(
    private _id: string,
    private _quantity: number,
    // Null when the current user lacks the `view_rate` permission and the backend
    // omits the value.
    private _rate: Nullable<number>,
    private _rate_type: string,
    private _total_amount: number,
    private _billing_adjustment_minutes: number,
    private _pay_start_time: Nullable<Dayjs>,
    private _pay_end_time: Nullable<Dayjs>,
    private _invoice_id: string,
    private _load: Nullable<LoadBasic>,
    private _name: Nullable<string>,
    private _line_item_type: InvoiceLineItemType,
    private _material_rate_unit_of_measure: Nullable<OrderUnitOfMeasure>,
    private _material: Nullable<BasicMaterial>,
    private _billing_state: BillingState | null = null,
    private _rejected_reason: RejectedReason | null = null,
    private _editable: boolean | null = null,
    private _settlement: SettlementBasic | null = null,
    private _derived_rate: number | null = null,
    private _delivered_quantity: number | null = null,
    private _pay_commission_amount: number = 0,
    private _taxed: boolean = false,
    private _tax_rate: number | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get quantity() {
    return this._quantity;
  }

  public get rate() {
    return this._rate;
  }

  public get rateType() {
    return this._rate_type;
  }

  public get totalAmount() {
    return this._total_amount;
  }

  public get billingAdjustmentMinutes() {
    return this._billing_adjustment_minutes;
  }

  public get payStartTime() {
    return this._pay_start_time;
  }

  public get payEndTime() {
    return this._pay_end_time;
  }

  public get invoiceId() {
    return this._invoice_id;
  }

  public get load() {
    return this._load;
  }

  public get name() {
    return this._name;
  }

  public get lineItemType() {
    return this._line_item_type;
  }

  public get materialRateUnitOfMeasure() {
    return this._material_rate_unit_of_measure;
  }

  public get material() {
    return this._material;
  }

  public get billingState() {
    return this._billing_state;
  }

  public get rejectedReason() {
    return this._rejected_reason;
  }

  public get editable() {
    return this._editable;
  }

  public get settlement() {
    return this._settlement;
  }

  public get derivedRate() {
    return this._derived_rate;
  }

  public get deliveredQuantity() {
    return this._delivered_quantity;
  }

  // Both rates are null without the `view_rate` permission, which is not a variance.
  public get hasRateVariance() {
    return (
      this._derived_rate !== null &&
      this._rate !== null &&
      !toDecimal(this._derived_rate).equals(toDecimal(this._rate))
    );
  }

  public get payCommissionAmount() {
    return this._pay_commission_amount;
  }

  public get taxed() {
    return this._taxed;
  }

  public get taxRate() {
    return this._tax_rate;
  }

  public static parse(proto: InvoiceLineItem_Read) {
    return new InvoiceLineItem(
      proto.id,
      Number(proto.quantity),
      isNil(proto.revenue_rate_value) ? null : Number(proto.revenue_rate_value),
      proto.revenue_cost_rate_type,
      Number(proto.total_amount),
      proto.billing_adjustment_minutes,
      proto.pay_start_time ? dayjs(proto.pay_start_time) : null,
      proto.pay_end_time ? dayjs(proto.pay_end_time) : null,
      proto.invoice_id,
      proto.load ? LoadBasic.parse(proto.load) : null,
      proto.name,
      proto.line_item_type,
      proto.material_rate_unit_of_measure,
      proto.material ? BasicMaterial.parse(proto.material) : null,
      proto.billing_state ?? null,
      proto.rejected_reason ?? null,
      proto.editable ?? null,
      proto.settlement ? SettlementBasic.parse(proto.settlement) : null,
      isNil(proto.derived_rate) ? null : Number(proto.derived_rate),
      Number(proto.delivered_quantity),
      Number(proto.pay_commission_amount),
      proto.taxed,
      isNil(proto.tax_rate) ? null : Number(proto.tax_rate),
    );
  }

  public static deparseUpdate(
    proto: Partial<InvoiceLineItem & { isDeleted?: boolean }>,
  ): InvoiceLineItem_Update {
    if (proto.isDeleted) {
      // i don't know wny this is necessary - proto.id is 100% a non-null string
      // but tsc can't convince itself of that :/
      return { id: proto.id, _destroy: 1 } as unknown as InvoiceLineItem_Update;
    }

    const deparsed: InvoiceLineItem_Update = {
      id: proto.id,
      quantity: proto.quantity,
      rate: proto.rate ?? undefined,
      pay_start_time: proto.payStartTime?.toString(),
      pay_end_time: proto.payEndTime?.toString(),
      billing_adjustment_minutes: proto.billingAdjustmentMinutes,
      ...(proto.load?.id ? { load_id: proto.load.id } : {}),
    };

    return deparsed;
  }
}

export class InvoiceLineItemNet {
  constructor(
    private _invoice_line_item_id: string,
    private _net_total_amount: Nullable<number>,
    private _pay_commission_percentage: Nullable<string>,
    private _pay_commission_net_rate: Nullable<string>,
  ) {}

  public get invoiceLineItemId() {
    return this._invoice_line_item_id;
  }

  public get netTotalAmount() {
    return this._net_total_amount;
  }

  public get payCommissionPercentage() {
    return this._pay_commission_percentage;
  }

  public get payCommissionNetRate() {
    return this._pay_commission_net_rate;
  }

  public static parse(proto: InvoiceLineItemNet_Read) {
    return new InvoiceLineItemNet(
      proto.invoice_line_item_id,
      isNil(proto.net_total_amount) ? null : Number(proto.net_total_amount),
      proto.pay_commission_percentage ?? null,
      proto.pay_commission_net_rate ?? null,
    );
  }
}

export class PayCommissionPresentation {
  constructor(
    private _commission_presentation: PayCommissionPresentation_Read['commission_presentation'],
    private _commission_summary: Nullable<CommissionSummary>,
    private _net_subtotal: number,
    private _line_items: InvoiceLineItemNet[],
  ) {
    this._line_items_by_id = new Map(
      _line_items.map((lineItem) => [lineItem.invoiceLineItemId, lineItem]),
    );
  }

  private _line_items_by_id: Map<string, InvoiceLineItemNet>;

  public get commissionPresentation() {
    return this._commission_presentation;
  }

  public get commissionSummary() {
    return this._commission_summary;
  }

  public get netSubtotal() {
    return this._net_subtotal;
  }

  public get showsNetColumns() {
    return (
      this._commission_presentation ===
        SettlementCommissionPresentation.NET_RATE_COLUMN &&
      this._line_items.some((lineItem) => !isNil(lineItem.netTotalAmount))
    );
  }

  public lineItemNet(invoiceLineItemId: string) {
    return this._line_items_by_id.get(invoiceLineItemId);
  }

  public static parse(proto: PayCommissionPresentation_Read) {
    return new PayCommissionPresentation(
      proto.commission_presentation,
      proto.commission_summary ? CommissionSummary.parse(proto.commission_summary) : null,
      Number(proto.net_subtotal),
      proto.line_items.map((lineItem) => InvoiceLineItemNet.parse(lineItem)),
    );
  }
}
